import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  FormGroup,
  FormControl
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';

// Services y Models
import { GuestReservationService } from '../../services/guest-reservation.service';
import { GuestReservationRequest } from '../../models/guest-reservation.model';
import { AuthService } from '../../services/auth.service';
import { HostingService } from '../../services/hosting.service';

// 🆕 INTERFAZ PARA SUGERENCIAS
interface RoomSuggestion {
  rooms: any[];
  totalCapacity: number;
  totalPrice: number;
  isValid: boolean;
  message: string;
}

@Component({
  selector: 'guest-booking',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatSnackBarModule,
    MatSelectModule,
    MatTabsModule,
    MatChipsModule,
    MatListModule,
    MatDividerModule,
    MatExpansionModule,
    MatTooltipModule
  ],
  templateUrl: './guest-booking.component.html',
  styleUrls: ['./guest-booking.component.css']
})
export class GuestBookingComponent implements OnInit {
  // Dependency Injection
  private fb = inject(FormBuilder);
  private reservationService = inject(GuestReservationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);
  private hostingService = inject(HostingService);
  private http = inject(HttpClient);

  // Signals
  isLoading = signal(false);
  submitSuccess = signal(false);
  organizationName = signal('');
  minCheckInDate = signal(new Date());
  minCheckOutDate = signal(new Date());

  // Query params
  orgId = signal('');
  landingSlug = signal('');
  accommodationId = signal('');
  accommodation = signal<any>(null);

  // 🆕 Datos de formulario
  guestForm: FormGroup;
  extraServices: any[] = [];
  availableRooms: any[] = [];
  selectedRooms: any[] = [];
  roomSuggestions: RoomSuggestion[] = [];
  showSuggestions = false;
  noRoomSuggestions = false;
  Step = 0; // 0: Datos básicos, 1: Seleccionar habitaciones, 2: Confirmación

  // Estados del formulario
  searchAvailabilityForm: FormGroup;

  ID_TYPES = [
    'Cédula de Ciudadanía',
    'Cédula de Extranjería',
    'Pasaporte',
    'Tarjeta de Identidad'
  ];

  // Computed signals
  numberOfNights = computed(() => {
    const checkIn = this.searchAvailabilityForm.get('check_in')?.value;
    const checkOut = this.searchAvailabilityForm.get('check_out')?.value;
    if (!checkIn || !checkOut) return 0;
    return this.reservationService.calculateNights(checkIn, checkOut);
  });

  totalGuests = computed(() => {
    const adults = this.searchAvailabilityForm.get('adults')?.value || 0;
    const children = this.searchAvailabilityForm.get('children')?.value || 0;
    return adults + children;
  });

  constructor() {
    // � FORMULARIO DE DATOS BÁSICOS (EMAIL, TELÉFONO, FECHAS, PERSONAS)
    this.guestForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      phone: ['']
    });

    // 🔍 BÚSQUEDA DE DISPONIBILIDAD
    this.searchAvailabilityForm = this.fb.group({
      check_in: ['', Validators.required],
      check_out: ['', Validators.required],
      adults: [1, [Validators.required, Validators.min(1)]],
      children: [0, [Validators.min(0)]],
      rooms: [1, [Validators.required, Validators.min(1)]]
    });
  }

  ngOnInit(): void {
    // Capturar query params y cargar alojamiento
    this.route.queryParams.subscribe(params => {
      const orgIdParam = params['org'];
      const landingParam = params['landing'];
      const nameParam = params['name'];
      const accommodationParam = params['accommodation_id'];

      console.log('📍 Query params recibidos:', { orgIdParam, landingParam, nameParam, accommodationParam });

      if (!orgIdParam) {
        this.snackBar.open('❌ Enlace inválido - Falta parámetro: org', 'Cerrar', { duration: 5000 });
        this.router.navigate(['/']);
        return;
      }

      if (!accommodationParam) {
        this.snackBar.open('❌ Error: No se especificó el alojamiento. Por favor contacta con soporte.', 'Cerrar', { duration: 5000 });
        console.error('⚠️ accommodation_id no disponible en query params');
        setTimeout(() => this.router.navigate(['/']), 3000);
        return;
      }

      this.orgId.set(orgIdParam);
      this.landingSlug.set(landingParam || '');
      this.organizationName.set(nameParam || 'Nuestro alojamiento');
      this.accommodationId.set(accommodationParam);

      console.log('🎯 ALOJAMIENTO A CARGAR:', {
        accommodation_id: accommodationParam,
        org: orgIdParam,
        landing: landingParam,
        name: nameParam
      });

      // Cargar datos del alojamiento
      console.log('🔄 Cargando alojamiento con ID:', accommodationParam);
      this.loadAccommodation(accommodationParam);
    });

    // Configurar fecha mínima
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.minCheckInDate.set(today);

    this.searchAvailabilityForm.get('check_in')?.valueChanges.subscribe(checkIn => {
      if (checkIn) {
        const nextDay = new Date(checkIn);
        nextDay.setDate(nextDay.getDate() + 1);
        this.minCheckOutDate.set(nextDay);

        const currentCheckOut = this.searchAvailabilityForm.get('check_out')?.value;
        if (currentCheckOut && currentCheckOut <= checkIn) {
          this.searchAvailabilityForm.patchValue({ check_out: null });
        }
      }
    });
  }

  // 🆕 CARGAR ALOJAMIENTO
  private loadAccommodation(accommodationId: string): void {
    console.log('🌐 Llamando API getHostingsByid con ID:', accommodationId, 'Tipo:', typeof accommodationId);

    this.hostingService.getHostingsByid(Number(accommodationId)).subscribe({
      next: (response: any) => {
        console.log('✅ RESPUESTA COMPLETA DE API:', response);

        // El API devuelve { items: [...], hasMore: false }
        let accommodation: any = null;

        if (response.items && Array.isArray(response.items) && response.items.length > 0) {
          // Si viene un array, buscar el alojamiento que coincida con el ID solicitado
          accommodation = response.items.find((item: any) =>
            item.id === accommodationId ||
            item.id === String(accommodationId) ||
            String(item.id) === accommodationId
          );

          // Si no encuentra coincidencia exacta, tomar el primero como fallback
          if (!accommodation) {
            console.warn('⚠️ No se encontró alojamiento con ID exacto. Tomando el primero como fallback');
            accommodation = response.items[0];
          }

          console.log('📦 Array de alojamientos encontrado. Usando:', accommodation?.name, 'ID:', accommodation?.id);
        } else if (response.rooms) {
          // Si viene un objeto directo con rooms
          accommodation = response;
          console.log('🏨 Alojamiento individual encontrado:', accommodation?.name);
        }

        if (!accommodation) {
          console.error('❌ No se encontró alojamiento en la respuesta');
          this.snackBar.open('❌ No se encontró el alojamiento', 'Cerrar', { duration: 3000 });
          return;
        }

        console.log('🏨 Alojamiento final a usar:', {
          nombre: accommodation.name,
          id: accommodation.id,
          capacidad: accommodation.capacity
        });

        console.log('📋 TODOS LOS CAMPOS DEL ALOJAMIENTO:', accommodation);

        this.accommodation.set(accommodation);

        // Extraer habitaciones del alojamiento seleccionado
        const rooms = accommodation.rooms || [];
        console.log('🛏️ Habitaciones del alojamiento:', rooms);
        console.log('📊 Cantidad de habitaciones:', rooms.length);

        // Mapear y validar las habitaciones
        this.availableRooms = rooms.map((room: any, index: number) => ({
          ...room,
          name: room.type ? `${room.type} - Habitación ${room.number || index + 1}` : `Habitación ${room.number || index + 1}`,
          capacity: Number(room.capacity) || 1,
          price_per_night: Number(room.price_per_night) || 0
        }));

        console.log('✅ Habitaciones procesadas y listas:', this.availableRooms);
        console.log('📋 Resumen:', {
          habitaciones_totales: this.availableRooms.length,
          primer_habitacion: this.availableRooms[0]?.name,
          precios: this.availableRooms.map(r => ({ nombre: r.name, precio: r.price_per_night, capacidad: r.capacity }))
        });

        this.extraServices = (accommodation.extra_services || accommodation.additional_services || []).map((service: any) => ({
          ...service,
          quantity: 0,
          cost: parseFloat(service.cost) || 0
        }));
      },
      error: (error: any) => {
        console.error('❌ Error cargando alojamiento:', error);
        console.error('📍 Error completo:', JSON.stringify(error, null, 2));
        this.snackBar.open('❌ Error cargando el alojamiento', 'Cerrar', { duration: 3000 });
      }
    });
  }



  // ✅ CONTINUAR CON DATOS BÁSICOS
  continueToAvailability(): void {
    if (this.guestForm.invalid) {
      this.snackBar.open('Por favor ingresa tu email', 'Cerrar', { duration: 3000 });
      return;
    }
    this.Step = 1;
  }

  // 🔍 BÚSQUEDA DE DISPONIBILIDAD
  searchAvailability(): void {
    const totalPeople = this.getTotalPeople();
    const requestedRooms = Number(this.searchAvailabilityForm.get('rooms')?.value || 1);

    console.log('🔍 Buscando disponibilidad...');
    console.log('👥 Total de personas:', totalPeople);
    console.log('🏨 Habitaciones disponibles en sistema:', this.availableRooms);
    console.log('📦 Cantidad de habitaciones cargadas:', this.availableRooms.length);

    if (!this.accommodation()) {
      this.snackBar.open('Error: No se cargó el alojamiento', 'Cerrar', { duration: 3000 });
      return;
    }

    let rooms = this.availableRooms.map((room: any) => ({
      ...room,
      name: room.type ? `${room.type} - Habitación ${room.number}` : `Habitación ${room.number}`,
      capacity: Number(room.capacity) || 0,
      price_per_night: Number(room.price_per_night) || 0
    }));

    console.log('🛏️ Habitaciones procesadas:', rooms);

    this.roomSuggestions = this.generateRoomSuggestions(rooms, totalPeople, requestedRooms);
    console.log('✅ Sugerencias generadas:', this.roomSuggestions);

    // noRoomSuggestions = true solo si NO hay sugerencias en absoluto
    this.noRoomSuggestions = this.roomSuggestions.length === 0;

    this.showSuggestions = true;

    if (requestedRooms === 1) {
      this.searchAvailabilityForm.get('rooms')?.enable();
    } else {
      this.searchAvailabilityForm.get('rooms')?.disable();
    }
  }

  // 🆕 GENERAR SUGERENCIAS DE HABITACIONES
  private generateRoomSuggestions(
    rooms: any[],
    totalPeople: number,
    requestedRooms: number
  ): RoomSuggestion[] {
    const suggestions: RoomSuggestion[] = [];

    if (requestedRooms === 1) {
      // CASO 1: Una sola habitación
      rooms.forEach(room => {
        if (room.capacity >= totalPeople) {
          suggestions.push({
            rooms: [room],
            totalCapacity: room.capacity,
            totalPrice: room.price_per_night,
            isValid: true,
            message: `✅ ${room.name} - Capacidad: ${room.capacity} personas`
          });
        }
      });

      // Si no hay coincidencias exactas, mostrar la más grande disponible
      if (suggestions.length === 0 && rooms.length > 0) {
        const largest = rooms.reduce((prev, current) =>
          (prev.capacity > current.capacity) ? prev : current
        );
        suggestions.push({
          rooms: [largest],
          totalCapacity: largest.capacity,
          totalPrice: largest.price_per_night,
          isValid: false,
          message: `⚠️ ${largest.name} - Capacidad insuficiente (${largest.capacity} para ${totalPeople})`
        });
      }
    } else if (requestedRooms > 1) {
      // CASO 2: Múltiples habitaciones
      const combinations = this.findRoomCombinations(rooms, totalPeople, requestedRooms);
      combinations.forEach(combo => {
        const totalCapacity = combo.reduce((sum: number, room: any) => sum + room.capacity, 0);
        const totalPrice = combo.reduce((sum: number, room: any) => sum + room.price_per_night, 0);

        suggestions.push({
          rooms: combo,
          totalCapacity,
          totalPrice,
          isValid: totalCapacity >= totalPeople,
          message: totalCapacity >= totalPeople
            ? `✅ ${combo.length} habitaciones - Capacidad total: ${totalCapacity} personas`
            : `⚠️ ${combo.length} habitaciones - Capacidad: ${totalCapacity} (se necesita ${totalPeople})`
        });
      });
    }

    // Separar sugerencias válidas e inválidas
    const validSuggestions = suggestions.filter(s => s.isValid).slice(0, 5);
    const invalidSuggestions = suggestions.filter(s => !s.isValid).slice(0, 5);

    // Si hay sugerencias válidas, devolver solo esas
    // Si no hay válidas pero hay inválidas, devolver las inválidas (warning)
    // Si no hay nada, devolver array vacío
    return validSuggestions.length > 0 ? validSuggestions : invalidSuggestions;
  }

  private findRoomCombinations(rooms: any[], totalPeople: number, numRooms: number): any[][] {
    const combinations: any[][] = [];

    const generateCombos = (current: any[], start: number) => {
      if (current.length === numRooms) {
        combinations.push([...current]);
        return;
      }

      for (let i = start; i < rooms.length; i++) {
        current.push(rooms[i]);
        generateCombos(current, i + 1);
        current.pop();
      }
    };

    generateCombos([], 0);

    return combinations
      .sort((a, b) => {
        const priceA = a.reduce((sum: number, r: any) => sum + r.price_per_night, 0);
        const priceB = b.reduce((sum: number, r: any) => sum + r.price_per_night, 0);
        return priceA - priceB;
      })
      .slice(0, 10);
  }

  // 🆕 SELECCIONAR SUGERENCIA
  selectSuggestion(suggestion: RoomSuggestion): void {
    if (!suggestion.isValid) {
      // Mostrar warning pero permitir continuar
      this.snackBar.open('⚠️ ATENCIÓN: La capacidad de habitación puede ser insuficiente', 'OK', { duration: 4000 });
    }
    this.selectedRooms = suggestion.rooms;
    this.Step = 2;
  }

  // 🆕 VERIFICAR SI UNA SUGERENCIA ESTÁ SELECCIONADA
  isSuggestionSelected(suggestion: RoomSuggestion): boolean {
    if (this.selectedRooms.length === 0) return false;
    if (suggestion.rooms.length !== this.selectedRooms.length) return false;

    return suggestion.rooms.every(room =>
      this.selectedRooms.some(selected => selected.id === room.id)
    );
  }

  // 👥 AGREGAR HUÉSPED
  // 💰 CÁLCULOS
  calculateNights(): number {
    const checkIn = this.searchAvailabilityForm.get('check_in')?.value;
    const checkOut = this.searchAvailabilityForm.get('check_out')?.value;

    if (!checkIn || !checkOut) return 0;

    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const diff = checkOutDate.getTime() - checkInDate.getTime();
    const nights = Math.ceil(diff / (1000 * 3600 * 24));

    return nights > 0 ? nights : 0;
  }

  calculateRoomTotal(): number {
    const nights = this.calculateNights();
    if (this.selectedRooms.length > 0) {
      return this.selectedRooms.reduce((sum, room) => sum + (room.price_per_night || 0), 0) * nights;
    }
    return 0;
  }

  calculateServicesTotal(): number {
    return this.extraServices.reduce((sum, service) => sum + (service.cost * service.quantity), 0);
  }

  calculateSubtotal(): number {
    return this.calculateRoomTotal() + this.calculateServicesTotal();
  }

  calculateTotal(): number {
    return this.calculateSubtotal();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  incrementService(serviceId: string): void {
    const service = this.extraServices.find(s => s.id === serviceId);
    if (service) service.quantity++;
  }

  decrementService(serviceId: string): void {
    const service = this.extraServices.find(s => s.id === serviceId);
    if (service && service.quantity > 0) service.quantity--;
  }

  getTotalPeople(): number {
    const adults = this.searchAvailabilityForm.get('adults')?.value || 0;
    const children = this.searchAvailabilityForm.get('children')?.value || 0;
    return adults + children;
  }

  // 🆕 ENVIAR RESERVA CON FORMATO CORRECTO
  async onSubmit(): Promise<void> {
    if (this.guestForm.invalid) {
      this.snackBar.open('⚠️ Por favor ingresa tu email', 'OK', { duration: 3000 });
      return;
    }

    if (this.selectedRooms.length === 0) {
      this.snackBar.open('⚠️ Debes seleccionar una habitación', 'OK', { duration: 3000 });
      return;
    }

    if (this.searchAvailabilityForm.invalid) {
      this.snackBar.open('⚠️ Por favor completa todos los campos', 'OK', { duration: 3000 });
      return;
    }

    this.isLoading.set(true);

    try {
      const checkIn = this.searchAvailabilityForm.get('check_in')?.value;
      const checkOut = this.searchAvailabilityForm.get('check_out')?.value;

      // Convertir fechas a formato YYYY-MM-DD
      const checkInDate = checkIn instanceof Date ? checkIn.toISOString().split('T')[0] : checkIn;
      const checkOutDate = checkOut instanceof Date ? checkOut.toISOString().split('T')[0] : checkOut;

      // Calcular noches
      const nights = this.calculateNights();

      // Obtener datos del guest
      const guestEmail = this.guestForm.get('email')?.value;
      const guestPhone = this.guestForm.get('phone')?.value || '';

      // 🆕 BUSCAR INFORMACIÓN COMPLETA DEL CLIENTE POR EMAIL
      let clientInfo: any = null;
      try {
        console.log('🔍 Buscando información del cliente por email:', guestEmail);
        const response: any = await this.http
          .get(`https://a0x9hrcvu7.execute-api.us-east-1.amazonaws.com/dev/auth/user?email=${guestEmail}`)
          .toPromise();

        console.log('✅ Respuesta completa del endpoint:', response);

        // El endpoint retorna { success, timestamp, user: {...} }
        // Extraer el objeto user si existe, sino usar toda la respuesta
        clientInfo = response?.user || response;
        console.log('✅ Información del cliente extraída:', clientInfo);
      } catch (error: any) {
        console.warn('⚠️ No se encontró información del cliente (no crítico):', error);
        // Continuar sin error
      }

      // 🆕 USAR EL ID DEL PROPIETARIO DEL ALOJAMIENTO
      const ownerUserId = this.accommodation()?.user_id || '';
      console.log('👤 Owner user_id del alojamiento:', ownerUserId);

      // Obtener el ID de la primera habitación seleccionada
      const firstRoom = this.selectedRooms.length > 0 ? this.selectedRooms[0] : null;
      const firstRoomId = firstRoom?.id || '';

      // Calcular valores
      const subtotal = this.calculateSubtotal();
      const total = this.calculateTotal();

      // Crear cliente con la información obtenida del endpoint o fallback
      const client = clientInfo || {
        id: guestEmail,
        email: guestEmail,
        emailVerified: false,
        givenName: guestEmail?.split('@')[0] || 'Guest',
        familyName: '',
        phone: guestPhone
      };

      // Obtener servicios extra que fueron seleccionados
      const extra_services = this.extraServices
        .filter(s => s.quantity > 0)
        .map(s => ({
          id: s.id,
          name: s.name,
          unit_price: s.cost,
          quantity: s.quantity
        }));

      // Crear payload en formato que espera el backend
      const payload = {
        accommodation_id: this.accommodationId(),
        room_id: firstRoomId,
        status: 'Pendiente',
        check_in: checkInDate,
        check_out: checkOutDate,
        client: client,
        selectedRooms: this.selectedRooms,
        extra_services: extra_services,
        payment: {
          id: '',
          subtotal_value: subtotal,
          total_value: total
        },
        subtotal_value: subtotal,
        total_value: total,
        user_id: ownerUserId // ID del propietario del alojamiento
      };

      console.log('📤 Enviando reserva:', payload);

      // Enviar al servidor
      this.reservationService.createReservation(payload as any).subscribe({
        next: (response) => {
          console.log('✅ Reserva creada:', response);
          this.submitSuccess.set(true);
          this.isLoading.set(false);

          this.snackBar.open('✅ ¡Solicitud enviada! Te contactaremos pronto', 'OK', {
            duration: 5000,
            panelClass: ['success-snackbar']
          });

          setTimeout(() => {
            this.router.navigate(['/']);
          }, 3000);
        },
        error: (error) => {
          console.error('❌ Error:', error);
          this.isLoading.set(false);

          this.snackBar.open(
            `❌ ${error.message || 'Error al enviar solicitud'}`,
            'Reintentar',
            { duration: 6000 }
          );
        }
      });
    } catch (error: any) {
      console.error('❌ Error al procesar la reserva:', error);
      this.isLoading.set(false);
      this.snackBar.open('Error al procesar la reserva', 'OK', { duration: 3000 });
    }
  }

  onCancel(): void {
    this.router.navigate(['/']);
  }

  hasError(field: string, errorType: string): boolean {
    const control = this.searchAvailabilityForm.get(field);
    return !!(control?.hasError(errorType) && control?.touched);
  }

  getErrorMessage(field: string): string {
    const control = this.searchAvailabilityForm.get(field);
    if (!control?.errors || !control?.touched) return '';

    if (control.hasError('required')) return 'Campo obligatorio';
    if (control.hasError('email')) return 'Email inválido';
    if (control.hasError('minlength')) {
      const minLength = control.errors['minlength'].requiredLength;
      return `Mínimo ${minLength} caracteres`;
    }

    return 'Campo inválido';
  }
}

